import type { MouseEventHandler } from "react";
import className from "~/kernel/styling/className";
import Spinner from "./Spinner";
import { noop } from "~/kernel/functions/noop";

export type ButtonProps = {
  type?: "button" | "submit";
  children: React.ReactNode;
  onClick?: MouseEventHandler;
  form?: string;
  disabled?: boolean;
  tabIndex?: number;
  "aria-label"?: string;
} & ButtonClassesProps;

type ButtonSize = "medium" | "small";

type ButtonVariant =
  | "tertiary"
  | "primary"
  | "secondary"
  | "muted"
  | "text"
  | "positive";

export type ButtonClassesProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  round?: "all" | "right" | "left" | "none";
};

export function buttonClasses({
  variant = "primary",
  loading = false,
  size = "medium",
  disabled = false,
  fullWidth = true,
  round = "all",
}: ButtonClassesProps) {
  const baseClasses = className(
    "inline-block text-center whitespace-nowrap transition relative active:duration-[0ms] duration-100",
    {
      "cursor-pointer": !disabled,
      "rounded-0": round === "none",
      "rounded-md": round === "all",
      "rounded-r": round === "right",
      "rounded-l": round === "left",
      "w-full": fullWidth,
      "text-xs px-3 py-2": size === "small",
      "px-4 py-3": size === "medium",
      "opacity-50": disabled || loading,
    },
  );

  switch (variant) {
    case "primary":
      return className(
        `${baseClasses} bg-primary text-white border-primary border active:bg-pink-300 active:border-pink-300`,
        {
          "hover:bg-pink-400": !disabled,
          "hover:border-pink-400": !disabled,
        },
      );
    case "secondary":
      return className(
        `${baseClasses} border border-slate-300 bg-white active:text-slate-300 active:border-slate-300`,
        { "hover:border-slate-600": !disabled },
      );
    case "muted":
      return className(
        `${baseClasses} bg-slate-100 text-slate-800 border-slate-100 border active:bg-slate-100 active:text-slate-400 active:border-slate-100`,
        {
          "hover:bg-slate-200": !disabled,
          "hover:border-slate-200": !disabled,
        },
      );
    case "text":
      return className("text-secondary", { "opacity-50": disabled });
    case "tertiary":
      return className(
        `${baseClasses} border-transparent border active:bg-slate-100 active:border-slate-i100`,
        {
          "hover:bg-slate-100 hover:border-slate-100": !disabled,
        },
      );
    case "positive":
      return className(
        `${baseClasses} bg-positive text-white border-positive border active:bg-green-400 active:border-green-400`,
        {
          "hover:bg-green-500": !disabled,
          "hover:border-green-500": !disabled,
        },
      );
  }
}

export default function Button(props: ButtonProps) {
  const {
    children,
    type = "button",
    onClick = noop,
    loading = false,
    disabled = false,
    form,
    "aria-label": ariaLabel,
    tabIndex,
  } = props;
  return (
    <button
      form={form}
      type={type}
      className={buttonClasses(props)}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      tabIndex={tabIndex}
    >
      <div
        className={className(
          "pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 transition-all",
          {
            "-translate-y-6 opacity-0": !loading,
            "-translate-y-3": loading,
          },
        )}
      >
        <Spinner />
      </div>
      <span
        className={className({
          "translate-y-3 opacity-0": loading,
          "translate-y-0 opacity-100": !loading,
        })}
      >
        {children}
      </span>
    </button>
  );
}

Button.defaultProps = {
  variant: "primary",
  type: "button",
  size: "medium",
  onClick: noop,
  loading: false,
};
